import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../../assets/scss/pages/_reports.scss";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import CommonTable from "components/ReactTable/Table";
import {
  DEFAULT_RECORDS_PER_PAGE,
  fixedWidthCustomStyle,
} from "components/Common/constants";
import { formatDateLocal } from "components/Common/commonFunctions";
import {
  export_dashboard_report,
  get_dashboard_report,
} from "API";
import { getCustomerList } from "actions/customer";
import NotificationAlert from "react-notification-alert";

const REPORT_TYPES = [
  { value: "REPORT_CUSTOMER_WISE_SALES", label: "Customer Wise Sales", dateField: "startEnd" },
  { value: "REPORT_PENDING_TO_PART_ON_ORDER", label: "Pending to Part On Order", dateField: "CreatedAt" },
  { value: "REPORT_PENDING_TO_READY_FOR_PICKUP", label: "Pending to Ready for Pickup", dateField: "CreatedAt" },
  { value: "REPORT_PART_ON_ORDER", label: "Part On Order", dateField: "CreatedAt" },
  { value: "REPORT_PART_ON_ORDER_TO_READY_FOR_PICKUP", label: "Part On Order to Ready for Pickup", dateField: "CreatedAt" },
  { value: "REPORT_READY_FOR_PICKUP_TO_REPAIRED", label: "Ready for Pickup to Repaired", dateField: "CreatedAt" },
];

const getReportRows = (payload) => {
  const findRows = (value, depth = 0) => {
    if (!value || depth > 5) return [];
    if (Array.isArray(value)) {
      return value.length === 0 || value.some((item) => item && typeof item === "object")
        ? value
        : [];
    }
    if (typeof value !== "object") return [];
    const preferredKeys = ["report", "rows", "job", "jobs", "records", "result", "data"];
    for (const key of preferredKeys) {
      const found = findRows(value[key], depth + 1);
      if (found.length) return found;
    }
    for (const nested of Object.values(value)) {
      const found = findRows(nested, depth + 1);
      if (found.length) return found;
    }
    return [];
  };
  return findRows(payload);
};

const getReportTotal = (payload, rows) => Number(
  payload?.data?.totalFilteredRecords ??
  payload?.data?.totalRecords ??
  payload?.data?.totalCount ??
  payload?.data?.total ??
  payload?.data?.count ??
  payload?.totalFilteredRecords ??
  payload?.totalRecords ??
  payload?.totalCount ??
  payload?.total ??
  payload?.count ??
  rows.length,
);

const getCellValue = (value) => {
  if (value == null || value === "") return "—";
  if (Array.isArray(value)) return value.length ? value.map(getCellValue).join(", ") : "—";
  if (typeof value === "object") return Object.values(value).filter(Boolean).map(getCellValue).join(" · ") || "—";
  return String(value);
};


const formatWorkOrderStatus = (value) => {
  const formattedValue = getCellValue(value);
  const fullStatus = formattedValue.replace(/\bJOB(?=_|\b)/g, "WORK_ORDER");
  const colors = {   text: "#616161" };
  return (
    <span
      title={fullStatus === "—" ? undefined : fullStatus}
      style={{
        display: "inline-block",
        maxWidth: "100%",
        padding: "3px 9px",
        border: `1px solid ${colors.border}`,
        borderRadius: 999,
        backgroundColor: colors.bg,
        color: colors.text,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {fullStatus}
    </span>
  );
};

const renderEmailCell = (value) => {
  const email = getCellValue(value);
  if (email === "—") return email;
  return (
    <span
      title={email}
      style={{
        display: "block",
        width: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {email}
    </span>
  );
};

const renderStackedCell = (value) => {
  if (!value || typeof value !== "object") return getCellValue(value);
  const lines = Object.entries(value).filter(([, item]) => item != null && item !== "");
  if (!lines.length) return "—";
  return (
    <div className="d-flex flex-column">
      {lines.map(([key, line]) => {
        const label = { from: "From", to: "To", name: "Name", phone: "Phone" }[key];
        return <span key={key}>{label ? `${label}: ` : ""}{getCellValue(line)}</span>;
      })}
    </div>
  );
};

const renderCustomerCell = (value) => {
  if (!value) return "—";
  const name = String(value);
  return (
    <span
      title={name}
      style={{
        display: "block",
        maxWidth: 210,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {name}
    </span>
  );
};

const WORK_ORDER_COLUMNS = [
  { id: "ID", exportKeys: ["ID"], label: "Work Order No.", width: 150, value: (row) => row.ID },
  { id: "customerName", exportKeys: ["customer.companyName"], label: "Customer", width: 240, value: (row) => row.customer?.companyName || row.customer?.name || `${row.customer?.firstName || ""} ${row.customer?.lastName || ""}`.trim() },
  { id: "customerLocation", exportKeys: ["customerLocation.outletName"], label: "Customer Location", width: 230, value: (row) => row.customerLocation?.outletName },
  { id: "contact", exportKeys: ["customerLocation.contactPersonName", "customerLocation.contactPersonPhone"], label: "Contact Info", width: 230, value: (row) => ({ name: row.customerLocation?.contactPersonName, phone: row.customerLocation?.contactPersonPhone }) },
  { id: "technician", exportKeys: ["technician.name"], label: "Technician", width: 200, value: (row) => row.technician?.name },
  { id: "paymentTerm", exportKeys: ["paymentTerm.term"], label: "Payment Term", width: 165, value: (row) => row.paymentTerm?.term },
  { id: "amount", exportKeys: ["paymentTerm.amount"], label: "Amount", width: 130, value: (row) => row.paymentTerm?.amount },
  { id: "schedule", exportKeys: ["scheduledFrom", "scheduledTo"], label: "Schedule", width: 280, value: (row) => ({ from: row.scheduledFrom, to: row.scheduledTo }) },
  { id: "jobStatus", exportKeys: ["jobStatus"], label: "Status", width: 230, value: (row) => row.jobStatus, format: formatWorkOrderStatus },
  { id: "partRequestedStatus", exportKeys: ["partRequestedStatus"], label: "Parts Order Status", width: 220, value: (row) => row.partRequestedStatus, format: formatWorkOrderStatus },
  { id: "createdAt", exportKeys: ["createdAt"], label: "Created At", width: 200, value: (row) => row.createdAt },
];

const readPath = (row, path) => path.split(".").reduce((value, key) => value?.[key], row);

const formatReportColumnLabel = (path) => path
  .split(".")
  .map((part) => part.replace(/([a-z0-9])([A-Z])/g, "$1 $2"))
  .join(" / ")
  .replace(/^./, (letter) => letter.toUpperCase())
  .replace(/\s*\/\s*term\b/i, "")
  .replace(/\bNo\b/g, "No.");

const getCustomerSalesName = (row) => {
  const customer = row.customer || row;
  return row.companyName || customer.companyName || row.customerName || customer.name ||
    [customer.firstName || row.customerFirstName, customer.lastName || row.customerLastName].filter(Boolean).join(" ") || "";
};

const REPORT_DURATION_COLUMNS = {
  REPORT_PENDING_TO_PART_ON_ORDER: {
    id: "pendingToPartOnOrder.totalPendingTime",
    label: "Pending to Part On Order",
    value: (row) => row.pendingToPartOnOrder?.totalPendingTime,
  },
  REPORT_PART_ON_ORDER: {
    id: "partOnOrder.totalPartOnOrderTime",
    label: "Part On Order Duration",
    value: (row) => row.partOnOrder?.totalPartOnOrderTime,
  },
  REPORT_PENDING_TO_READY_FOR_PICKUP: {
    id: "pendingToReadyForPickup.totalDuration",
    label: "Pending to Ready for Pickup",
    value: (row) => row.pendingToReadyForPickup?.totalDuration,
  },
  REPORT_PART_ON_ORDER_TO_READY_FOR_PICKUP: {
    id: "partOnOrderToReadyForPickup.totalDuration",
    label: "Part On Order to Ready for Pickup",
    value: (row) => row.partOnOrderToReadyForPickup?.totalDuration,
  },
  REPORT_READY_FOR_PICKUP_TO_REPAIRED: {
    id: "readyForPickupToRepaired.totalDuration",
    label: "Ready for Pickup to Repaired",
    value: (row) => row.readyForPickupToRepaired?.totalDuration,
  },
};

const CUSTOMER_EXPORT_FIELDS = `odooID,customerName,amountUntaxed,amountTax,amountTotal,orderCount`.split(",");

const JOB_EXPORT_FIELDS = `ID,customer.ID,customer.name,customer.email,customer.phone,customer.companyName,customer.totalSales,customerLocation.ID,customerLocation.name,customerLocation.regionResponce.ID,customerLocation.regionResponce.name,customerLocation.regionResponce.status,customerLocation.outletName,customerLocation.contactPersonName,customerLocation.contactPersonPhone,customerLocation.contactEmail,customerLocation.companyName,customerLocation.alternateContactPersonName,customerLocation.alternateContactPersonPhone,customerLocation.alternateContactEmail,customerLocation.address,customerLocation.mapDetails.location_name,customerLocation.mapDetails.formattedAddress,customerLocation.mapDetails.placeID,customerLocation.mapDetails.locationType,customerLocation.mapDetails.coordinates.lat,customerLocation.mapDetails.coordinates.lng,jobTypeID.ID,jobTypeID.name,jobTypeID.status,technician.ID,technician.name,paymentTerm.ID,paymentTerm.term,paymentTerm.status,paymentTerm.description,paymentTerm.amount,scheduledFrom,scheduledTo,rescheduleReason,cancelledReason,underWarranty,jobReferenceNo,jobSalesOrderNo,partsPurchaseOrderNo,clientNote,adminNote,partsNote,warrantyNote,partRequestedReceivedOn,partRequestedEtaDate,priority,partRequestedStatus,jobStatus,jobDuration,createdByID,createdAt,completedOn,internalTrackingFlag,quotationStatus,pendingToPartOnOrder.pendingPeriods.pendingSince,pendingToPartOnOrder.pendingPeriods.pendingUntil,pendingToPartOnOrder.pendingPeriods.pendingTime,pendingToPartOnOrder.pendingPeriods.partOnOrderAt,pendingToPartOnOrder.totalPendingTime,partOnOrder.partOnOrderPeriods.partOnOrderSince,partOnOrder.partOnOrderPeriods.partOnOrderUntil,partOnOrder.partOnOrderPeriods.partOnOrderTime,partOnOrder.partOnOrderPeriods.readyForPickupAt,partOnOrder.totalPartOnOrderTime,pendingToReadyForPickup.transitionPeriods.startedAt,pendingToReadyForPickup.transitionPeriods.endedAt,pendingToReadyForPickup.transitionPeriods.duration,pendingToReadyForPickup.totalDuration,partOnOrderToReadyForPickup.transitionPeriods.startedAt,partOnOrderToReadyForPickup.transitionPeriods.endedAt,partOnOrderToReadyForPickup.transitionPeriods.duration,partOnOrderToReadyForPickup.totalDuration,readyForPickupToRepaired.transitionPeriods.startedAt,readyForPickupToRepaired.transitionPeriods.endedAt,readyForPickupToRepaired.transitionPeriods.duration,readyForPickupToRepaired.totalDuration`.split(",");

const buildReportColumns = (row, reportType) => {
  if (reportType === "REPORT_CUSTOMER_WISE_SALES") {
    const paths = [];
    const collectLeaves = (value, path = "") => {
      if (!value || typeof value !== "object" || Array.isArray(value)) {
        if (path && value !== undefined) paths.push(path);
        return;
      }
      Object.entries(value).forEach(([key, nested]) => {
        if (key === "mapDetails" || key === "coordinates" || key === "ID" || key === "customerID" || key === "CustomerID" || /address/i.test(key)) return;
        if (["userID", "firstName", "lastName", "customerFirstName", "customerLastName", "name", "companyName", "customerName", "scheduledFrom", "scheduledTo", "contactPersonName", "contactPersonPhone", "regionResponce", "jobTypeID", "quotationStatus", "jobSalesOrderNo", "priority"].includes(key)) return;
        collectLeaves(nested, path ? `${path}.${key}` : key);
      });
    };
    collectLeaves(row);
    const columns = paths.map((path) => {
      const width = 170;
      const leafName = path.split(".").pop();
      const label = leafName.toLowerCase() === "userid"
        ? "Customer ID"
        : formatReportColumnLabel(path);
      return {
        id: path,
        exportKeys: [path.split(".").pop()],
        Header: label,
        accessor: (item) => readPath(item, path),
        Cell: ({ value }) => leafName.toLowerCase().includes("email")
          ? renderEmailCell(value)
          : getCellValue(value),
        width,
        minWidth: width,
        maxWidth: width + 100,
      };
    });
    columns.unshift({
      id: "customerFullName",
      exportKeys: row.firstName || row.customer?.firstName
        ? ["companyName"]
        : row.customerName
          ? ["customerName"]
          :  ["firstName", "lastName"],
      Header: "Company Name",
      accessor: getCustomerSalesName,
      Cell: ({ value }) => renderCustomerCell(value),
      width: 240,
      minWidth: 240,
      maxWidth: 300,
    });

    if (row.scheduledFrom || row.scheduledTo) {
      columns.push({
        id: "schedule",
        exportKeys: ["scheduledFrom", "scheduledTo"],
        Header: "Schedule",
        accessor: (item) => ({ from: item.scheduledFrom, to: item.scheduledTo }),
        Cell: ({ value }) => renderStackedCell(value),
        width: 220,
        minWidth: 220,
        maxWidth: 300,
      });
    }
    if (row.customerLocation?.contactPersonName || row.customerLocation?.contactPersonPhone) {
      columns.push({
        id: "contactInfo",
        exportKeys: ["contactPersonName", "contactPersonPhone"],
        Header: "Contact Info",
        accessor: (item) => ({ name: item.customerLocation?.contactPersonName, phone: item.customerLocation?.contactPersonPhone }),
        Cell: ({ value }) => renderStackedCell(value),
        width: 220,
        minWidth: 220,
        maxWidth: 300,
      });
    }
    return columns;
  }

  const columns = WORK_ORDER_COLUMNS.map(({ id, label, width, value, exportKeys, format }) => ({
    id,
    exportKeys: exportKeys || [id],
    Header: label,
    accessor: value,
    Cell: ({ value }) => {
      if (id === "schedule" || id === "contact") return renderStackedCell(value);
      if (id === "customerName") return renderCustomerCell(value);
      if (format) return format(value);
      return getCellValue(value);
    },
    width,
    minWidth: width,
    maxWidth: width + 80,
  }));
  const durationColumn = REPORT_DURATION_COLUMNS[reportType];
  if (durationColumn) {
    columns.push({
      id: durationColumn.id,
      exportKeys: [durationColumn.id],
      Header: durationColumn.label,
      accessor: durationColumn.value,
      Cell: ({ value }) => getCellValue(value),
      width: 220,
      minWidth: 220,
      maxWidth: 300,
    });
  }
  return columns;

};

function Reports() {
  const dispatch = useDispatch();
  const notificationAlertRef = useRef(null);
  const customerListData = useSelector((state) => state.customer.customerList);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [reportType, setReportType] = useState(REPORT_TYPES[0]);
  const [appliedReport, setAppliedReport] = useState({
    report: REPORT_TYPES[0],
    startDate: null,
    endDate: null,
    customer: null,
    customerSearch: "",
  });
  const [pageNum, setPageNum] = useState(1);
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_RECORDS_PER_PAGE || 25);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(null);
  const [columnsModalOpen, setColumnsModalOpen] = useState(false);
  const [exportColumnsModalOpen, setExportColumnsModalOpen] = useState(false);
  const [selectedExportColumns, setSelectedExportColumns] = useState([]);

  const notify = (type, message) => {
    const options = {
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    };
    notificationAlertRef.current?.notificationAlert(options);
  };

  const makeRequestParams = (report, fromDate, toDate, page, selectedCustomer, customerSearchText) => {
    const params = { reportType: report.value, page };
    const filters = [];
    const trimmedCustomerSearch = customerSearchText?.trim();
    const isCustomerWiseSalesReport = report.value === "REPORT_CUSTOMER_WISE_SALES";

    if (isCustomerWiseSalesReport && trimmedCustomerSearch) {
      params.customerName = trimmedCustomerSearch;
    } else {
      const customerID = selectedCustomer?.ID ?? selectedCustomer?.id;
      if (customerID != null) {
        filters.push({
          field: "CustomerID",
          condition: "eq",
          filterValues: [String(customerID)],
        });
      }
    }

    if (fromDate && toDate) {
      const from = formatDateLocal(fromDate);
      const to = formatDateLocal(toDate);
      if (report.dateField === "startEnd") {
        params.startDate = from;
        params.endDate = to;
      } else {
        filters.push(
          { field: report.dateField, condition: "btw", filterValues: [from, to] },
        );
      }
    }
    if (filters.length) params.filtersJSON = JSON.stringify(filters);
    return params;
  };

  useEffect(() => {
    getCustomerList(dispatch, {
      filters: JSON.stringify([
        { field: "UserStatus", condition: "eq", filterValues: ["ACTIVE"] },
      ]),
    });
  }, [dispatch]);

  useEffect(() => {
    if (!appliedReport) return undefined;
    let active = true;
    setIsLoading(true);
    get_dashboard_report(makeRequestParams(
      appliedReport.report,
      appliedReport.startDate,
      appliedReport.endDate,
      pageNum,
      appliedReport.customer,
      appliedReport.customerSearch,
    ))
      .then((response) => {
        if (!active) return;
        const payload = response?.data;
        if (payload?.code != null && Number(payload.code) !== 200) {
          throw new Error(payload.message || "Could not load this report.");
        }
        const resultRows = getReportRows(payload);
        setRows(resultRows);
        setTotalRecords(getReportTotal(payload, resultRows));
        setPageSize(Number(payload?.data?.noOfRecordsPerPage || payload?.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE || 25));
       
      })
      .catch((error) => {
        if (!active) return;
        setRows([]);
        setTotalRecords(0);
        notify("danger", error?.response?.data?.message || error.message || "Could not load this report. Please try again.");
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => { active = false; };
  }, [appliedReport, pageNum]);

  const allColumns = useMemo(() => {
    if (!rows.length) return [];
    return buildReportColumns(rows[0], appliedReport.report.value);
  }, [rows, appliedReport]);
  const exportColumnOptions = useMemo(() => {
    if (appliedReport.report.value === "REPORT_CUSTOMER_WISE_SALES") {
      return CUSTOMER_EXPORT_FIELDS.map((field) => ({
        id: field,
        Header: formatReportColumnLabel(field),
        exportKeys: [field],
      }));
    }

    const visibleReportColumns = allColumns.length
      ? allColumns.map((column) => ({
        id: column.id,
        Header: column.Header,
        exportKeys: column.exportKeys || [column.id],
      }))
      : WORK_ORDER_COLUMNS.map(({ id, label, exportKeys }) => ({
        id,
        Header: label,
        exportKeys,
      }));
    const otherDurationColumns = Object.entries(REPORT_DURATION_COLUMNS)
      .filter(([reportType]) => reportType !== appliedReport.report.value)
      .map(([, column]) => ({
        id: column.id,
        Header: column.label,
        exportKeys: [column.id],
      }));
    const optionsById = new Map();
    [...visibleReportColumns, ...otherDurationColumns].forEach((column) => {
      if (!optionsById.has(column.id)) optionsById.set(column.id, column);
    });
    return Array.from(optionsById.values());
  }, [allColumns, appliedReport.report.value]);
  const requiredColumnId = appliedReport.report.value === "REPORT_CUSTOMER_WISE_SALES"
    ? null
    : "ID";
  const columns = useMemo(
    () => visibleColumns === null
      ? allColumns
      : allColumns.filter((column) => (
        visibleColumns.includes(column.id) ||
        column.id === requiredColumnId
      )),
    [allColumns, visibleColumns, requiredColumnId],
  );
  const allReportColumnsSelected = visibleColumns === null || allColumns.every((column) =>
    visibleColumns.includes(column.id),
  );
  const exportColumnsAreAllSelected = exportColumnOptions.every((column) =>
    selectedExportColumns.includes(column.id),
  );

  const handleReportTypeChange = (nextReport) => {
    setReportType(nextReport);
    if (!nextReport) return;
    setCustomer(null);
    setCustomerSearch("");
    setVisibleColumns(null);
    setPageNum(1);
    setAppliedReport({
      report: nextReport,
      startDate,
      endDate,
      customer: null,
      customerSearch: "",
    });
  };

  const handleSearch = () => {
    if (!reportType) {
      notify("warning", "Select a report type.");
      return;
    }
    if (startDate && endDate && startDate > endDate) {
      notify("warning", "Start date must be before the end date.");
      return;
    }
    setPageNum(1);
    setAppliedReport({
      report: reportType,
      startDate: startDate || null,
      endDate: endDate || null,
      customer: reportType?.value === "REPORT_CUSTOMER_WISE_SALES" ? null : customer,
      customerSearch: reportType?.value === "REPORT_CUSTOMER_WISE_SALES" ? customerSearch.trim() : "",
    });
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const isCustomerWiseSalesReport = reportType?.value === "REPORT_CUSTOMER_WISE_SALES";
  const hasSelectedFilters = Boolean(
    customer || customerSearch || startDate || endDate ||
    appliedReport?.customer || appliedReport?.customerSearch || appliedReport?.startDate || appliedReport?.endDate,
  );

  const handleClearFilters = () => {
    setCustomer(null);
    setCustomerSearch("");
    setStartDate(null);
    setEndDate(null);
    setPageNum(1);
    setAppliedReport({
      report: reportType || appliedReport.report,
      startDate: null,
      endDate: null,
      customer: null,
      customerSearch: "",
    });
  };

  const openDownloadColumns = () => {
    if (!appliedReport) {
      notify("warning", "Search for a report before downloading it.");
      return;
    }
    if (!exportColumnOptions.length) {
      notify("warning", "No columns are available to export.");
      return;
    }
    setSelectedExportColumns(exportColumnOptions.map((column) => column.id));
    setExportColumnsModalOpen(true);
  };

  const handleDownload = async () => {
    const allowedFields = appliedReport.report.value === "REPORT_CUSTOMER_WISE_SALES"
      ? CUSTOMER_EXPORT_FIELDS
      : JOB_EXPORT_FIELDS.filter((field) => (
        field !== "customer.ID" && !/address/i.test(field)
      ));
    const selectedColumns = exportColumnOptions
      .filter((column) => (
        selectedExportColumns.includes(column.id) ||
        column.id === requiredColumnId
      ))
      .flatMap((column) => column.exportKeys || [column.id])
      .filter((field) => allowedFields.includes(field));
    const uniqueSelectedColumns = [...new Set(selectedColumns)];
    if (!uniqueSelectedColumns.length) {
      notify("warning", "Select at least one column to export.");
      return;
    }
    setIsExporting(true);
    try {
      const reportFilters = makeRequestParams(
        appliedReport.report,
        appliedReport.startDate,
        appliedReport.endDate,
        1,
        appliedReport.customer,
        appliedReport.customerSearch,
      );
      const params = {
        ...reportFilters,
        columns: uniqueSelectedColumns.join(","),
      };
      delete params.page;
      const response = await export_dashboard_report(params);
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const contentType = response.headers?.["content-type"] || blob.type;
      if (contentType.includes("json")) {
        const errorPayload = JSON.parse(await blob.text());
        throw new Error(errorPayload.message || "CSV download failed.");
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const dateSuffix = appliedReport.startDate && appliedReport.endDate
        ? `_${formatDateLocal(appliedReport.startDate)}_${formatDateLocal(appliedReport.endDate)}`
        : "";
      const disposition = response.headers?.["content-disposition"] || "";
      const serverFilename = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i)?.[1];
      link.download = serverFilename
        ? decodeURIComponent(serverFilename.replace(/"/g, ""))
        : `${appliedReport.report.value.toLowerCase()}${dateSuffix}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setExportColumnsModalOpen(false);
      notify("success", "CSV downloaded successfully.");
    } catch (error) {
      notify("danger", error?.response?.data?.message || error.message || "CSV download failed. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const customerOptions = customerListData?.data?.customer || [];

  return (
    <div className="reports-page">
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      <Container fluid className="mt-4">
        <Row>
          <Col className="mb-5" md="12">
            <Card>
              <CardHeader className="d-flex align-items-center justify-content-between mb-2">
                <CardTitle tag="h3" className="font-weight-normal">Reports</CardTitle>
                <div className="d-flex align-items-center">
                  <Button
                    className="btn-simple btn-round btn-style mr-3"
                    color="default"
                    onClick={() => setColumnsModalOpen(true)}
                  >
                    <i className="fa-solid fa-table-columns mr-1" /> Columns
                  </Button>
                  <Button
                    className="btn-simple btn-round btn-style"
                    color="default"
                    onClick={openDownloadColumns}
                    disabled={isExporting || !appliedReport}
                  >
                    <i className="fas fa-download mr-1" />
                    {isExporting ? "Downloading..." : "Download CSV"}
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                <div className="d-flex flex-wrap align-items-center filter-group">
                  <Select
                    className="mr-2 mb-2 filter-select"
                    classNamePrefix="react-select"
                    options={REPORT_TYPES}
                    value={reportType}
                    onChange={handleReportTypeChange}
                    onKeyDown={handleKeyDown}
                    placeholder="Report type"
                    styles={fixedWidthCustomStyle(true)}
                  />
                  {isCustomerWiseSalesReport ? (
                    <Input
                      className="mr-2 mb-2 filter-select"
                      type="search"
                      value={customerSearch}
                      onChange={(event) => setCustomerSearch(event.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Search customer"
                      style={{ maxWidth: 260, minWidth: 220 }}
                    />
                  ) : (
                    <Select
                      className="mr-2 mb-2 filter-select"
                      classNamePrefix="react-select"
                      options={customerOptions}
                      getOptionLabel={(option) => {
                        const personName = `${option.firstName || ""} ${option.lastName || ""}`.trim();
                        return [option.companyName, personName].filter(Boolean).join(" - ") || option.name || "";
                      }}
                      getOptionValue={(option) => String(option.ID || option.id)}
                      value={customer}
                      onChange={setCustomer}
                      onKeyDown={handleKeyDown}
                      placeholder="Customer"
                      isClearable
                      styles={fixedWidthCustomStyle(true)}
                    />
                  )}
                  <DatePicker
                    selected={startDate}
                    onChange={setStartDate}
                    selectsStart
                    startDate={startDate}
                    endDate={endDate}
                    maxDate={endDate || new Date()}
                    isClearable
                    onKeyDown={handleKeyDown}
                    placeholderText="Start date"
                    wrapperClassName="filter-select mr-2 mb-2 reports-date-filter"
                    className="form-control"
                    dateFormat="dd-MM-yyyy"
                  />
                  <DatePicker
                    selected={endDate}
                    onChange={setEndDate}
                    selectsEnd
                    startDate={startDate}
                    endDate={endDate}
                    minDate={startDate}
                    maxDate={new Date()}
                    isClearable
                    onKeyDown={handleKeyDown}
                    placeholderText="End date"
                    wrapperClassName="filter-select mr-2 mb-2 reports-date-filter"
                    className="form-control"
                    dateFormat="dd-MM-yyyy"
                  />
                  <Button
                    color="default"
                    className="d-flex justify-content-center align-items-center mb-2 mt-0 filter-btn btn-simple"
                    onClick={handleSearch}
                    disabled={isLoading}
                  >
                    {isLoading ? <i className="fa fa-spinner fa-spin search-loader" /> : <><i className="fa fa-search mr-1" /> Search</>}
                  </Button>
                  {hasSelectedFilters && (
                    <Button
                      color="default"
                      className="d-flex justify-content-center align-items-center mb-2 mt-0 ml-2 filter-btn btn-simple"
                      onClick={handleClearFilters}
                      disabled={isLoading}
                    >
                      <i className="fa fa-times mr-1" /> Clear
                    </Button>
                  )}
                </div>
                {appliedReport && !isLoading && (
                  <div className="mt-2 mb-3 mx-1 font-weight-bold">
                    {appliedReport.report.label}: {totalRecords} records
                  </div>
                )}
                {isLoading ? (
                  <div className="loaderContainer"><i className="fa fa-spinner fa-spin fa-2xl loader" /></div>
                ) : rows.length === 0 ? (
                  <div className="loaderContainer text-muted">No records match the selected report filters.</div>
                ) : (
                  <div className="job-table">
                    <CommonTable
                      showPaginationBottom
                      defaultPageSize={pageSize}
                      activePage={pageNum}
                      data={rows}
                      fixedColumnWidths
                      columns={columns}
                      className="-striped -highlight table-hover table-fixed"
                      totalPages={totalPages}
                      setPageNum={setPageNum}
                    />
                  </div>
                )}
                <Modal isOpen={columnsModalOpen} toggle={() => setColumnsModalOpen(false)} className="reports-columns-modal">
                  <ModalHeader toggle={() => setColumnsModalOpen(false)}>Choose report columns</ModalHeader>
                  <ModalBody>
                    {allColumns.length ? (
                      <>
                        <p className="text-muted mb-3">Choose the columns to show in the report.</p>
                        <div className="reports-column-options">
                          {allColumns.map((column) => (
                            <Label key={column.id} className="reports-column-option">
                              <Input
                                type="checkbox"
                                checked={
                                  visibleColumns === null ||
                                  visibleColumns.includes(column.id) ||
                              column.id === requiredColumnId
                                }
                                disabled={column.id === requiredColumnId}
                                onChange={() => setVisibleColumns((current) => {
                                  if (column.id === requiredColumnId) return current;
                                  const selected = current === null ? allColumns.map((item) => item.id) : current;
                                  return selected.includes(column.id)
                                    ? selected.filter((id) => id !== column.id)
                                    : [...selected, column.id];
                                })}
                              />
                              <span>{column.Header}</span>
                            </Label>
                          ))}
                        </div>
                      </>
                    ) : <p className="text-muted mb-0">Columns will be available after report data loads.</p>}
                  </ModalBody>
                  <ModalFooter>
                    {!allReportColumnsSelected && (
                      <Button color="secondary" onClick={() => setVisibleColumns(null)}>
                        Reset Columns
                      </Button>
                    )}
                    <Button color="secondary" outline onClick={() => setColumnsModalOpen(false)}>Done</Button>
                  </ModalFooter>
                </Modal>
                <Modal
                  isOpen={exportColumnsModalOpen}
                  toggle={() => !isExporting && setExportColumnsModalOpen(false)}
                  className="reports-columns-modal"
                >
                  <ModalHeader toggle={() => !isExporting && setExportColumnsModalOpen(false)}>
                    Select CSV columns
                  </ModalHeader>
                  <ModalBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <span className="text-muted">Choose the columns to include in the CSV.</span>
                      <Button
                        color="link"
                        className="p-0"
                        onClick={(event) => {
                          setSelectedExportColumns(
                            exportColumnsAreAllSelected
                              ? (requiredColumnId ? [requiredColumnId] : [])
                              : exportColumnOptions.map((column) => column.id),
                          );
                          event.currentTarget.blur();
                        }}
                        disabled={isExporting}
                      >
                        {exportColumnsAreAllSelected ? "Clear all" : "Select all"}
                      </Button>
                    </div>
                    <div className="reports-column-options">
                      {exportColumnOptions.map((column) => (
                        <Label key={column.id} className="reports-column-option">
                          <Input
                            type="checkbox"
                            checked={selectedExportColumns.includes(column.id) || column.id === requiredColumnId}
                            disabled={isExporting || column.id === requiredColumnId}
                            onChange={() => setSelectedExportColumns((current) => current.includes(column.id)
                              ? current.filter((id) => id !== column.id)
                              : [...current, column.id])}
                          />
                          <span>{column.Header}</span>
                        </Label>
                      ))}
                    </div>
                  </ModalBody>
                  <ModalFooter>
                    <Button
                      color="secondary"
                      onClick={() => setExportColumnsModalOpen(false)}
                      disabled={isExporting}
                    >
                      Cancel
                    </Button>
                    <Button  color="secondary"
                      outline onClick={handleDownload} disabled={isExporting}>
                      {isExporting ? "Downloading..." : "Download CSV"}
                    </Button>
                  </ModalFooter>
                </Modal>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default Reports;
